/**
 * One Team's running system, read once and on demand.
 *
 *   loading  the first read is in flight
 *   off      the server answered 404: running copies are off for this
 *            workspace, or the reader cannot open the Team; render nothing
 *   ready    `data` is the record
 *   error    the read failed for another reason; the section stays quiet
 */
import { useCallback, useEffect, useRef, useState } from "react";
import type { SystemEnvironmentReadResponse } from "@yagni/shared";
import type { SystemApi } from "./systemApi.js";

export type TeamSystemStatus = "loading" | "off" | "ready" | "error";

export function useTeamSystem(api: SystemApi, teamId: string) {
  const [status, setStatus] = useState<TeamSystemStatus>("loading");
  const [data, setData] = useState<SystemEnvironmentReadResponse | null>(null);
  const latest = useRef(0);

  const reread = useCallback(async () => {
    const call = ++latest.current;
    try {
      const next = await api.readTeam(teamId);
      if (call !== latest.current) return;
      setData(next);
      setStatus(next ? "ready" : "off");
    } catch {
      if (call !== latest.current) return;
      setStatus((prev) => (prev === "ready" ? prev : "error"));
    }
  }, [api, teamId]);

  useEffect(() => {
    setStatus("loading");
    setData(null);
    void reread();
    return () => {
      latest.current += 1;
    };
  }, [reread]);

  return { status, data, reread };
}
